// Depuración de Inventario: revisión guiada de productos duplicados
// (mismo producto, distinto código). Muestra por cada código su
// actividad real (compras XML y consumo en boletas) y permite
// Desactivar / Editar / Eliminar directamente sobre inv_productos.
//
// Acceso: mismo permiso que el Catálogo de Productos (perm_ver_catalogo o perm_vob).

import React, { useEffect, useState } from 'react';
import { Sparkles, Loader2, Ban, RotateCcw, Pencil, Trash2, X, Save, Search } from 'lucide-react';
import { supabase } from '../../supabase';
import { buildApiUrl } from '../../utils/apiUrl';
import { useEmpresaId } from '../../context/EmpresaContext';
import { useAppPermisos } from '../../hooks/useAppPermisos';
import { pageTitleCls, pageSubtitleCls, btnPrimary, btnDanger, inputCls, labelCls } from '../../components/ui';

interface CodigoDepuracion {
  id?: number;
  codigo: string;
  encontrado: boolean;
  descripcion?: string;
  nombre_app?: string | null;
  unidad_medida?: string | null;
  stock_actual?: number;
  activo?: boolean;
  entradas?: number;
  salidas?: number;
  ultima_compra?: string | null;
  ultimo_consumo?: string | null;
  usos_boletas?: number;
  ultima_boleta?: string | null;
  estado?: 'sugerido_activo' | 'sugerido_desactivar' | 'bloqueado' | 'revisar_manual' | 'no_encontrado';
  motivo?: string;
}

interface GrupoDepuracion {
  producto: string;
  bloqueado: boolean;
  motivo_bloqueo: string | null;
  posible_falso_positivo?: boolean;
  codigos: CodigoDepuracion[];
}

async function authHeaders(empresaId: number) {
  const { data: { session } } = await supabase.auth.getSession();
  return {
    'Content-Type': 'application/json',
    'x-empresa-id': String(empresaId),
    ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
  };
}

function fechaCorta(iso: string | null | undefined) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString('es-CR', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
}

function detalleCodigo(c: CodigoDepuracion): string {
  if (!c.encontrado) return 'No se encontró este código en el catálogo.';
  if (c.motivo) return c.motivo;
  const partes: string[] = [];
  partes.push(c.entradas ? `${c.entradas} compra(s), última ${fechaCorta(c.ultima_compra)}` : 'sin compras registradas');
  partes.push(c.salidas ? `${c.salidas} consumo(s), último ${fechaCorta(c.ultimo_consumo)}` : 'sin consumo registrado');
  return partes.join(' · ');
}

export function DepuracionInventarioPage() {
  const empresaId = useEmpresaId();
  const { perm_vob, perm_ver_catalogo } = useAppPermisos();
  const puedeVer = perm_vob || perm_ver_catalogo;

  const [grupos, setGrupos] = useState<GrupoDepuracion[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [procesando, setProcesando] = useState<number | null>(null);
  const [editando, setEditando] = useState<CodigoDepuracion | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const [fusionando, setFusionando] = useState<GrupoDepuracion | null>(null);
  const [guardandoFusion, setGuardandoFusion] = useState(false);

  async function cargar() {
    setLoading(true);
    try {
      const resp = await fetch(buildApiUrl('/aplicaciones/catalogo/depuracion'), { headers: await authHeaders(empresaId) });
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(j?.error || 'No se pudo cargar');
      setGrupos(j.grupos || []);
    } catch (e: any) {
      setToast({ msg: e?.message || 'Error al cargar', ok: false });
    } finally {
      setLoading(false);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (puedeVer) cargar(); }, [empresaId, puedeVer]);

  async function desactivar(c: CodigoDepuracion) {
    if (!c.id) return;
    setProcesando(c.id);
    try {
      const resp = await fetch(buildApiUrl(`/aplicaciones/catalogo/productos/${c.id}`), {
        method: 'PUT', headers: await authHeaders(empresaId), body: JSON.stringify({ activo: false }),
      });
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(j?.error || 'No se pudo desactivar');
      setToast({ msg: `Código ${c.codigo} desactivado.`, ok: true });
      await cargar();
    } catch (e: any) {
      setToast({ msg: e?.message || 'Error al desactivar', ok: false });
    } finally {
      setProcesando(null);
    }
  }

  async function reactivar(c: CodigoDepuracion) {
    if (!c.id) return;
    setProcesando(c.id);
    try {
      const resp = await fetch(buildApiUrl(`/aplicaciones/catalogo/productos/${c.id}`), {
        method: 'PUT', headers: await authHeaders(empresaId), body: JSON.stringify({ activo: true }),
      });
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(j?.error || 'No se pudo reactivar');
      setToast({ msg: `Código ${c.codigo} reactivado.`, ok: true });
      await cargar();
    } catch (e: any) {
      setToast({ msg: e?.message || 'Error al reactivar', ok: false });
    } finally {
      setProcesando(null);
    }
  }

  async function eliminar(c: CodigoDepuracion) {
    if (!c.id) return;
    if (!window.confirm(`¿Eliminar el código ${c.codigo} definitivamente? Esto solo funciona si nunca tuvo movimientos.`)) return;
    setProcesando(c.id);
    try {
      const resp = await fetch(buildApiUrl(`/aplicaciones/catalogo/productos/${c.id}`), {
        method: 'DELETE', headers: await authHeaders(empresaId),
      });
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(j?.error || 'No se pudo eliminar');
      setToast({ msg: `Código ${c.codigo} eliminado.`, ok: true });
      await cargar();
    } catch (e: any) {
      setToast({ msg: e?.message || 'Error al eliminar', ok: false });
    } finally {
      setProcesando(null);
    }
  }

  async function fusionar(origenId: number, destinoId: number) {
    setGuardandoFusion(true);
    try {
      const resp = await fetch(buildApiUrl('/aplicaciones/catalogo/depuracion/fusionar'), {
        method: 'POST', headers: await authHeaders(empresaId),
        body: JSON.stringify({ origen_id: origenId, destino_id: destinoId }),
      });
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(j?.error || 'No se pudo fusionar');
      setToast({ msg: `Fusionado. Nuevo saldo: ${j.stock_destino}. Boletas reasignadas: ${j.boletas_reasignadas}.`, ok: true });
      setFusionando(null);
      await cargar();
    } catch (e: any) {
      setToast({ msg: e?.message || 'Error al fusionar', ok: false });
    } finally {
      setGuardandoFusion(false);
    }
  }

  async function guardarEdicion(descripcion: string, unidad_medida: string) {
    if (!editando?.id) return;
    setProcesando(editando.id);
    try {
      const resp = await fetch(buildApiUrl(`/aplicaciones/catalogo/productos/${editando.id}`), {
        method: 'PUT', headers: await authHeaders(empresaId),
        body: JSON.stringify({ descripcion_detallada: descripcion, unidad_medida }),
      });
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(j?.error || 'No se pudo guardar');
      setToast({ msg: `Código ${editando.codigo} actualizado.`, ok: true });
      setEditando(null);
      await cargar();
    } catch (e: any) {
      setToast({ msg: e?.message || 'Error al guardar', ok: false });
    } finally {
      setProcesando(null);
    }
  }

  const totalCodigos = grupos.reduce((n, g) => n + g.codigos.filter(c => c.encontrado).length, 0);
  const resueltos = grupos.reduce((n, g) => n + g.codigos.filter(c => c.encontrado && c.activo === false).length, 0);

  const term = busqueda.trim().toLowerCase();
  const gruposFiltrados = term
    ? grupos.filter(g =>
        g.producto.toLowerCase().includes(term) ||
        g.codigos.some(c => c.codigo.toLowerCase().includes(term) || (c.descripcion || '').toLowerCase().includes(term))
      )
    : grupos;

  if (!puedeVer) {
    return (
      <div className="p-6">
        <h1 className={pageTitleCls}>Depuración de Inventario</h1>
        <p className={pageSubtitleCls}>Esta pantalla requiere acceso al catálogo de productos.</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles size={18} style={{ color: 'var(--emp-accent-txt)' }} />
        <h1 className={pageTitleCls}>Depuración de Inventario</h1>
      </div>
      <p className={pageSubtitleCls}>
        Productos con código repetido en el catálogo. El código con uso y compra reciente se
        deja activo; el otro se desactiva. Si un grupo está bloqueado, necesita una decisión
        de ingeniería antes de tocarlo.
      </p>

      {!loading && (
        <div className="mt-4 mb-3 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--ink-faint)' }} />
            <input
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
              placeholder="Buscar producto o código…"
              className={inputCls}
              style={{ paddingLeft: '30px' }}
            />
          </div>
          {totalCodigos > 0 && (
            <span className="text-xs" style={{ color: 'var(--ink-faint)' }}>
              {gruposFiltrados.length} de {grupos.length} grupos · {resueltos} de {totalCodigos} códigos desactivados
            </span>
          )}
        </div>
      )}

      {loading ? (
        <div className="mt-8 flex items-center gap-2 text-sm" style={{ color: 'var(--ink-faint)' }}>
          <Loader2 size={16} className="animate-spin" /> Cargando…
        </div>
      ) : (
        <div className="mt-4 space-y-4">
          {gruposFiltrados.map(g => (
            <div key={g.producto} className="rounded-lg border p-4" style={{ borderColor: g.bloqueado ? 'var(--tone-warn-border)' : 'var(--line)', background: g.bloqueado ? 'var(--tone-warn-bg)' : 'var(--surface-raised)' }}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold" style={{ color: g.bloqueado ? 'var(--tone-warn-text)' : 'var(--ink)' }}>
                  {g.bloqueado && '🔒 '}{g.producto}
                </h3>
                <span className="text-[11px]" style={{ color: 'var(--ink-faint)' }}>{g.codigos.length} código{g.codigos.length > 1 ? 's' : ''}</span>
              </div>

              {g.bloqueado && g.motivo_bloqueo && (
                <div className="mb-3">
                  <p className="text-xs mb-2" style={{ color: 'var(--tone-warn-text)' }}>{g.motivo_bloqueo}</p>
                  {g.codigos.filter(c => c.encontrado).length === 2 && (
                    <button onClick={() => setFusionando(g)}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded text-[11px] font-semibold border"
                      style={{ borderColor: 'var(--tone-warn-border)', color: 'var(--tone-warn-text)', background: 'var(--surface-raised)' }}>
                      Fusionar códigos…
                    </button>
                  )}
                </div>
              )}
              {g.posible_falso_positivo && (
                <p className="text-xs mb-3" style={{ color: 'var(--tone-warn-text)' }}>
                  ⚠️ Posible falso positivo: dos o más códigos tienen compra y consumo propios y activos — probablemente son productos distintos que solo comparten el nombre (misma marca, distinta fórmula), no un duplicado real. Revisá la descripción completa antes de desactivar cualquiera.
                </p>
              )}

              <div className="space-y-2">
                {g.codigos.map(c => (
                  <div key={c.codigo} className="flex items-center justify-between gap-3 rounded px-3 py-2 flex-wrap"
                    style={{ background: 'var(--surface-overlay)', opacity: c.encontrado && c.activo === false ? 0.55 : 1 }}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs font-bold px-1.5 py-0.5 rounded border" style={{ borderColor: 'var(--line)', color: 'var(--ink)' }}>{c.codigo}</span>
                        {c.estado === 'sugerido_activo' && (
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: 'var(--tone-success-bg)', color: 'var(--tone-success-text)' }}>sugerido: dejar activo</span>
                        )}
                        {c.estado === 'sugerido_desactivar' && (
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: 'var(--tone-warn-bg)', color: 'var(--tone-warn-text)' }}>sugerido: desactivar</span>
                        )}
                        {c.estado === 'revisar_manual' && (
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: 'var(--surface-raised)', color: 'var(--ink-faint)' }}>revisar a mano</span>
                        )}
                        {c.encontrado && c.activo === false && (
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: 'var(--surface-raised)', color: 'var(--ink-faint)' }}>desactivado</span>
                        )}
                      </div>
                      <div className="text-xs mt-0.5" style={{ color: 'var(--ink-muted)' }}>{c.descripcion || c.nombre_app || ''}</div>
                      <div className="text-[11px] mt-0.5" style={{ color: 'var(--ink-faint)' }}>{detalleCodigo(c)}</div>
                    </div>

                    {!c.encontrado ? null : g.bloqueado ? (
                      <span className="text-[11px] italic" style={{ color: 'var(--tone-warn-text)' }}>bloqueado</span>
                    ) : (
                      <div className="flex gap-1.5 flex-none">
                        {c.activo === false ? (
                          <button onClick={() => reactivar(c)} disabled={procesando === c.id}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded text-[11px] font-semibold border"
                            style={{ borderColor: 'var(--line)', color: 'var(--ink-muted)' }}>
                            <RotateCcw size={12} /> Reactivar
                          </button>
                        ) : (
                          <button onClick={() => desactivar(c)} disabled={procesando === c.id}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded text-[11px] font-semibold border"
                            style={{ borderColor: 'var(--tone-warn-border)', color: 'var(--tone-warn-text)', background: 'var(--tone-warn-bg)' }}>
                            <Ban size={12} /> Desactivar
                          </button>
                        )}
                        <button onClick={() => setEditando(c)} disabled={procesando === c.id}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded text-[11px] font-semibold border"
                          style={{ borderColor: 'var(--line)', color: 'var(--ink-muted)' }}>
                          <Pencil size={12} /> Editar
                        </button>
                        <button onClick={() => eliminar(c)} disabled={procesando === c.id}
                          className={btnDanger + ' !px-2.5 !py-1.5 !text-[11px] inline-flex items-center gap-1'}>
                          <Trash2 size={12} /> Eliminar
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          {grupos.length === 0 && (
            <p className="text-sm" style={{ color: 'var(--ink-faint)' }}>No se encontraron productos con código repetido en el catálogo.</p>
          )}
          {grupos.length > 0 && gruposFiltrados.length === 0 && (
            <p className="text-sm" style={{ color: 'var(--ink-faint)' }}>Ningún grupo coincide con "{busqueda}".</p>
          )}
        </div>
      )}

      {editando && (
        <ModalEditar
          codigo={editando}
          procesando={procesando === editando.id}
          onClose={() => setEditando(null)}
          onGuardar={guardarEdicion}
        />
      )}

      {fusionando && (
        <ModalFusionar
          grupo={fusionando}
          guardando={guardandoFusion}
          onClose={() => setFusionando(null)}
          onFusionar={fusionar}
        />
      )}

      {toast && (
        <div className="fixed bottom-5 right-5 z-50 px-4 py-3 rounded-xl shadow-2xl text-sm font-medium"
          style={{
            background: toast.ok ? 'var(--tone-success-bg)' : 'var(--tone-warn-bg)',
            border: `1px solid ${toast.ok ? 'var(--tone-success-border)' : 'var(--tone-warn-border)'}`,
            color: toast.ok ? 'var(--tone-success-text)' : 'var(--tone-warn-text)',
          }}>
          {toast.msg}
          <button className="ml-3 opacity-70" onClick={() => setToast(null)}>✕</button>
        </div>
      )}
    </div>
  );
}

function ModalEditar({ codigo, procesando, onClose, onGuardar }: {
  codigo: CodigoDepuracion; procesando: boolean; onClose: () => void; onGuardar: (descripcion: string, unidad: string) => void;
}) {
  const [descripcion, setDescripcion] = useState(codigo.descripcion || '');
  const [unidad, setUnidad] = useState(codigo.unidad_medida || '');

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-5" style={{ background: 'rgba(2,6,23,0.78)', backdropFilter: 'blur(5px)' }}
      onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-md rounded-xl p-5" style={{ background: 'var(--surface-base)', border: '1px solid var(--line)' }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>Editar código {codigo.codigo}</h3>
          <button onClick={onClose}><X size={16} style={{ color: 'var(--ink-faint)' }} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className={labelCls}>Descripción</label>
            <input className={inputCls} value={descripcion} onChange={e => setDescripcion(e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Unidad de medida</label>
            <input className={inputCls} value={unidad} onChange={e => setUnidad(e.target.value)} />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-3 py-2 rounded text-xs" style={{ color: 'var(--ink-faint)' }}>Cancelar</button>
          <button onClick={() => onGuardar(descripcion, unidad)} disabled={procesando} className={btnPrimary}>
            <Save size={13} className="inline mr-1.5 -mt-0.5" />
            {procesando ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ModalFusionar({ grupo, guardando, onClose, onFusionar }: {
  grupo: GrupoDepuracion; guardando: boolean; onClose: () => void; onFusionar: (origenId: number, destinoId: number) => void;
}) {
  const encontrados = grupo.codigos.filter(c => c.encontrado && c.id);
  // El que ya se usa en boletas es el candidato natural a sobrevivir —
  // cambiarlo rompería la trazabilidad de lo ya aplicado en el campo.
  // usos_boletas depende de que "articulo" en app_boleta_productos coincida
  // exactamente con el código — no siempre es así (puede venir vacío o con
  // otro formato). "salidas" viene directo de inv_movimientos por producto_id,
  // así que es la señal confiable de cuál código es el que realmente se usó.
  const sugeridoDestino =
    encontrados.find(c => (c.salidas || 0) > 0) ||
    encontrados.find(c => (c.usos_boletas || 0) > 0) ||
    encontrados[0];
  const [destinoId, setDestinoId] = useState<number | undefined>(sugeridoDestino?.id);
  const origen = encontrados.find(c => c.id !== destinoId);
  const destino = encontrados.find(c => c.id === destinoId);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-5" style={{ background: 'rgba(2,6,23,0.78)', backdropFilter: 'blur(5px)' }}
      onMouseDown={e => { if (e.target === e.currentTarget && !guardando) onClose(); }}>
      <div className="w-full max-w-lg rounded-xl p-5" style={{ background: 'var(--surface-base)', border: '1px solid var(--line)' }}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>Fusionar códigos — {grupo.producto}</h3>
          <button onClick={onClose}><X size={16} style={{ color: 'var(--ink-faint)' }} /></button>
        </div>
        <p className="text-xs mb-4" style={{ color: 'var(--ink-muted)' }}>
          Elegí cuál código se mantiene (destino). Todos los movimientos del otro código se
          mueven al que elijas, su saldo se recalcula, y el que sobra queda desactivado con
          saldo en cero. Las boletas/plan que apuntaban al código que se retira se reapuntan
          automáticamente. Esta acción no se puede deshacer con un clic.
        </p>

        <div className="space-y-2 mb-4">
          {encontrados.map(c => (
            <label key={c.codigo} className="flex items-start gap-2.5 rounded px-3 py-2.5 cursor-pointer"
              style={{ background: 'var(--surface-overlay)', border: c.id === destinoId ? '1px solid var(--tone-success-border)' : '1px solid transparent' }}>
              <input type="radio" name="destino" className="mt-1" checked={c.id === destinoId} onChange={() => setDestinoId(c.id)} />
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs font-bold">{c.codigo}</span>
                  {c.id === destinoId
                    ? <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: 'var(--tone-success-bg)', color: 'var(--tone-success-text)' }}>se mantiene (destino)</span>
                    : <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: 'var(--tone-warn-bg)', color: 'var(--tone-warn-text)' }}>se retira (origen)</span>}
                </div>
                <div className="text-xs mt-0.5" style={{ color: 'var(--ink-muted)' }}>{c.descripcion}</div>
                <div className="text-[11px] mt-0.5" style={{ color: 'var(--ink-faint)' }}>{detalleCodigo(c)}</div>
              </div>
            </label>
          ))}
        </div>

        {origen && destino && (
          <p className="text-xs mb-4" style={{ color: 'var(--ink-faint)' }}>
            Se moverán los movimientos de <strong>{origen.codigo}</strong> hacia <strong>{destino.codigo}</strong>,
            que quedará con el saldo recalculado. {origen.codigo} quedará desactivado con saldo 0.
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button onClick={onClose} disabled={guardando} className="px-3 py-2 rounded text-xs" style={{ color: 'var(--ink-faint)' }}>Cancelar</button>
          <button onClick={() => origen?.id && destino?.id && onFusionar(origen.id, destino.id)} disabled={guardando || !origen || !destino} className={btnPrimary}>
            {guardando ? 'Fusionando…' : 'Fusionar'}
          </button>
        </div>
      </div>
    </div>
  );
}
